/**
 * S-96 帳票一括作成ジョブ（F-ADM-07 / R-17）。担当: G4
 * 自分の依頼（基金は全件）の状態・進捗を表示し、完了したジョブの ZIP をダウンロードする。
 * kintone 版「帳票一括作成後(63)」アプリの代替。
 */
import { useState } from 'react';
import { Download, Files, RefreshCw } from 'lucide-react';
import { Button, DataTable, EmptyState, ErrorState, PageHeader, StatusBadge, useToast, type BadgeTone, type Column } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { formatDateTime } from '@/lib/format';
import { useReportJobs, type ReportJobDto, type ReportJobStatus } from './api';
import './report-jobs.css';

const STATUS_TONE: Record<ReportJobStatus, BadgeTone> = { 待機: 'neutral', 処理中: 'info', 完了: 'success', 失敗: 'danger' };

export function ReportJobListPage() {
  const user = useCurrentUser();
  const { year } = useFiscalYear();
  const toast = useToast();
  const [allYears, setAllYears] = useState(false);
  const { data, isPending, isFetching, error, refetch } = useReportJobs(allYears ? undefined : year);
  const [downloading, setDownloading] = useState<number | null>(null);

  const download = async (job: ReportJobDto) => {
    setDownloading(job.id);
    try {
      const name = await downloadFile(`/report-jobs/${job.id}/download`, { fallbackFilename: job.fileName ?? 'report.zip' });
      toast.success('ZIP をダウンロードしました', name);
    } catch (e) {
      toast.error('ダウンロードできませんでした', errorMessage(e));
    } finally {
      setDownloading(null);
    }
  };

  const columns: Column<ReportJobDto>[] = [
    { key: 'id', header: '受付番号', numeric: true, sortable: true, width: 90 },
    { key: 'createdAt', header: '依頼日時', sortable: true, width: 150, render: (j) => <span className="bs-num">{formatDateTime(j.createdAt)}</span> },
    {
      key: 'reportName',
      header: '帳票',
      sortable: true,
      width: 220,
      render: (j) => (
        <span>
          <span className="bs-jobs-id bs-num">{j.reportId}</span>
          {j.reportName}
        </span>
      ),
    },
    { key: 'fiscalYear', header: '年度', width: 64, align: 'right', render: (j) => <span className="bs-num">{j.fiscalYear ?? ''}</span> },
    ...(user.organizationType === '基金' ? [{ key: 'createdByName', header: '依頼者', width: 130 } as Column<ReportJobDto>] : []),
    { key: 'status', header: '状態', width: 90, sortable: true, render: (j) => <StatusBadge tone={STATUS_TONE[j.status]} dot>{j.status}</StatusBadge> },
    {
      key: 'progress',
      header: '進捗',
      width: 140,
      render: (j) => {
        const total = j.totalCount ?? 0;
        const pct = total > 0 ? Math.round((j.processedCount / total) * 100) : j.status === '完了' ? 100 : 0;
        return (
          <div className="bs-jobs-progress" aria-label={`進捗 ${j.processedCount} / ${total}`}>
            <span className="bs-jobs-progress__bar">
              <span style={{ width: `${pct}%` }} className={j.status === '失敗' ? 'is-failed' : undefined} />
            </span>
            <span className="bs-num">
              {j.processedCount}/{j.totalCount ?? '—'}
            </span>
          </div>
        );
      },
    },
    { key: 'finishedAt', header: '完了日時', width: 150, render: (j) => <span className="bs-num">{formatDateTime(j.finishedAt) || '—'}</span> },
    {
      key: 'action',
      header: '',
      render: (j) =>
        j.status === '完了' ? (
          <Button size="sm" variant="accent" icon={<Download size={14} />} loading={downloading === j.id} onClick={() => void download(j)}>
            ZIP ダウンロード
          </Button>
        ) : j.status === '失敗' ? (
          <span className="bs-jobs-error" title={j.errorMessage ?? ''}>
            {j.errorMessage}
          </span>
        ) : (
          <span className="bs-jobs-wait">作成中です。しばらくお待ちください</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="帳票出力（各種様式）"
        title="帳票一括作成ジョブ"
        description="定時連絡送信書・速報・報告書兼請求書・助成金領収書・台紙の一括作成依頼の状況です。完了した依頼は ZIP でダウンロードできます。"
        actions={
          <>
            <Button size="sm" variant={allYears ? 'primary' : 'secondary'} onClick={() => setAllYears((v) => !v)}>
              {allYears ? '全年度を表示中' : '選択年度のみ表示中'}
            </Button>
            <Button size="sm" icon={<RefreshCw size={14} />} loading={isFetching} onClick={() => void refetch()}>
              最新の状態に更新
            </Button>
          </>
        }
      />
      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          caption="帳票一括作成ジョブ"
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(j) => j.id}
          loading={isPending}
          pagination={{ pageSize: 30 }}
          empty={
            <EmptyState
              compact
              icon={<Files />}
              title="一括作成の依頼はありません"
              description="定時連絡・速報・請求書などの一覧画面の「帳票一括作成」から依頼できます。"
            />
          }
        />
      )}
    </>
  );
}
